// File: /pages/ShuffleMaps/Locations.jsx
"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import styles from "./Locations.module.css";
import ListingCard from "/components/Shufflemaps/Listings/ListingCard";

const CONTINENTS = [
  { code: "AF", name: "Africa" },
  { code: "AS", name: "Asia" },
  { code: "EU", name: "Europe" },
  { code: "NA", name: "North America" },
  { code: "OC", name: "Oceania" },
  { code: "SA", name: "South America" },
];

export default function LocationsPage() {
  const [summary, setSummary] = useState(
    CONTINENTS.map((c) => ({ ...c, listings: 0 }))
  );
  const [loadingSummary, setLoadingSummary] = useState(true);

  const [selected, setSelected] = useState(null); // { code, name }
  const [cards, setCards] = useState([]);
  const [cardsLoading, setCardsLoading] = useState(false);
  const [cardsError, setCardsError] = useState(null);
  const [pageInfo, setPageInfo] = useState({
    offset: 0,
    limit: 48,
    hasMore: false,
    total: 0,
  });

  const resultsRef = useRef(null);

  // Load continent summary counts
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch("/api/shufflemaps/locations/summary");
        const j = await r.json();
        if (!alive) return;
        if (Array.isArray(j?.continents)) {
          setSummary(
            j.continents.map((c) => ({
              code: c.code,
              name: c.name,
              listings: Number(c.listings) || 0,
            }))
          );
        }
      } catch {
        /* keep defaults */
      } finally {
        if (alive) setLoadingSummary(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const totalsByCode = useMemo(
    () => Object.fromEntries(summary.map((s) => [s.code, s.listings])),
    [summary]
  );

  // Fetch listing cards for a continent
  const fetchCards = async ({ code, append = false }) => {
    setCardsError(null);
    setCardsLoading(true);
    const nextOffset = append ? pageInfo.offset + pageInfo.limit : 0;

    try {
      const url =
        `/api/shufflemaps/locations/locationBasedListings` +
        `?continent=${encodeURIComponent(code)}` +
        `&limit=${pageInfo.limit}&offset=${nextOffset}`;
      const r = await fetch(url);
      const j = await r.json();
      if (!r.ok || j.error) throw new Error(j.message || j.error || "Bad response");

      setCards((prev) =>
        append ? [...prev, ...(j.listings || [])] : j.listings || []
      );
      setPageInfo({
        offset: j.offset || 0,
        limit: j.limit || pageInfo.limit,
        hasMore: !!j.hasMore,
        total: j.total || 0,
      });

      if (!append) {
        requestAnimationFrame(() =>
          resultsRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          })
        );
      }
    } catch (e) {
      setCardsError(e?.message || "Could not load listings");
    } finally {
      setCardsLoading(false);
    }
  };

  const onContinentClick = (c) => {
    setSelected(c);
    setPageInfo((p) => ({ ...p, offset: 0 }));
    fetchCards({ code: c.code, append: false });
  };

  return (
    <main className={styles.smMain}>
      <section className={styles.smSection}>
        <h1 className={styles.smTitle}>Locations</h1>
        <p className={styles.smSub}>
          Browse listings by region. Drill down from continent → country →
          state/province → city.
        </p>

        <div className={styles.grid}>
          {CONTINENTS.map((c) => (
            <button
              key={c.code}
              type="button"
              className={`${styles.card} ${
                selected?.code === c.code ? styles.cardActive : ""
              }`}
              onClick={() => onContinentClick(c)}
              aria-pressed={selected?.code === c.code}
            >
              <div className={styles.cardTitle}>{c.name}</div>
              <div className={styles.cardMeta}>
                {loadingSummary
                  ? "Loading…"
                  : `${totalsByCode[c.code] || 0} listing${
                      (totalsByCode[c.code] || 0) === 1 ? "" : "s"
                    }`}
              </div>
              <div className={styles.cardNote}>
                {selected?.code === c.code ? "Selected" : "Click to drill down"}
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* Results */}
      <section ref={resultsRef} className={styles.resultsSection}>
        {selected ? (
          <>
            <div className={styles.resultsHeader}>
              <h2 className={styles.resultsTitle}>
                Listings in {selected.name}
              </h2>
              <div className={styles.resultsMeta}>
                {cardsLoading && cards.length === 0
                  ? "Loading…"
                  : `${pageInfo.total} total`}
              </div>
            </div>

            {cardsError && <div className={styles.errorBox}>{cardsError}</div>}

            <div className={styles.cardsGrid}>
              {cards.map((l) => (
                <Link
                  key={l.id}
                  href={{
                    pathname: "/ShuffleMaps",
                    query: { listingId: String(l.id) }, // open inside the main app page (map shell)
                  }}
                  className={styles.cardWrap}
                  title="Open listing"
                >
                  <ListingCard
                    listingId={l.id}
                    name={l.name}
                    status={l.status}
                    shortDescription={l.shortDescription}
                    suppressEmptyRatingDisplay
                  />
                </Link>
              ))}
            </div>

            {(pageInfo.hasMore || cardsLoading) && (
              <div className={styles.loadMoreRow}>
                <button
                  className={styles.loadMoreBtn}
                  disabled={cardsLoading}
                  onClick={() =>
                    fetchCards({ code: selected.code, append: true })
                  }
                >
                  {cardsLoading ? "Loading…" : "Load more"}
                </button>
              </div>
            )}
          </>
        ) : (
          <div className={styles.placeholder}>
            Select a region to see listings.
          </div>
        )}
      </section>
    </main>
  );
}
